"use client";

import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/shared/OnboardingCtaButton";

import type { FC, ButtonHTMLAttributes } from "react";
import { cn } from "utils";
import { LoadingButton } from "@/src/components/EmailLogin/LoadingButton";

const I18n = I18nUtil.createScoped(Translations);

export type OnboardingCtaSurface = "dark" | "light";

export interface OnboardingCtaButtonProps extends Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick" | "type"
> {
  /** Button label */
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Dark quiz surface vs light (Value prop) background */
  surface?: OnboardingCtaSurface;
  testId?: string;
  className?: string;
  footerClassName?: string;
  /** Wrap in default quiz footer padding (default true) */
  withFooter?: boolean;
}

function resolveEnabledClassName(surface: OnboardingCtaSurface): string {
  if (surface === "light") {
    return "primary_button text-content_inverse_primary";
  }
  return "bg-background_inverse_overlay100 text-content_primary";
}

function resolveInactiveClassName(surface: OnboardingCtaSurface): string {
  if (surface === "light") {
    return "primary_button text-content_inverse_primary";
  }
  return "bg-background_inverse_overlay70 text-content_secondary";
}

export const OnboardingCtaButton: FC<OnboardingCtaButtonProps> = ({
  label: _label,
  disabled = false,
  loading = false,
  surface = "dark",
  testId = "onboarding-cta-button",
  className,
  footerClassName,
  withFooter = true,
  onClick,
  type = "button",
}) => {
  const isInactive = disabled || loading;
  const label = _label ?? I18n.t("Continue");

  const button = (
    <LoadingButton
      type={type}
      data-testid={testId}
      disabled={disabled}
      loading={loading}
      onClick={onClick}
      className={cn(
        "button md_button w-full min-w-[80px] rounded-lg px-4 py-3 button_m",
        isInactive
          ? resolveInactiveClassName(surface)
          : resolveEnabledClassName(surface),
        className,
      )}
    >
      {label}
    </LoadingButton>
  );

  if (!withFooter) {
    return button;
  }

  return <div className={cn("shrink-0 p-4", footerClassName)}>{button}</div>;
};
